import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, KeyRound, Search, Copy, Upload, Download } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const lbl = "text-[10px] font-semibold uppercase tracking-wider text-slate-500";
const ROLES = [["md", "MD"], ["vp", "VP Sales"], ["zh", "Zonal Head"], ["sm", "Sales Manager"],
  ["sales", "KEC (Sales Consultant)"], ["tdc", "TDC"], ["security", "Security"],
  ["aho", "Accounts HO"], ["azh", "Accounts Zonal"], ["admin", "Admin"]];
const BRANCH_ROLES = ["sm", "tdc", "security", "sales"];
const ZONE_ROLES = ["zh", "azh"];
const bLabel = (b) => (b ? (b.dealer_code ? `${b.dealer_code} — ${b.name}` : b.name) : "");
const genPassword = () => "Kia@" + Math.random().toString(36).slice(2, 8);
const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "Never");

function CredsDialog({ creds, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(`Login: ${creds.username}\nPassword: ${creds.password}`); toast.success("Copied"); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div data-testid="creds-dialog" className="w-full max-w-sm rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-[#0E2240]">{creds.title}</h3>
        <p className="mt-1 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">Shown once — share with the user now. They must change this password at first login.</p>
        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm">
          <div><span className="text-slate-400 text-xs">Login:</span> <b data-testid="creds-username">{creds.username}</b></div>
          <div className="mt-1"><span className="text-slate-400 text-xs">Password:</span> <b data-testid="creds-password" className="font-mono">{creds.password}</b></div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={copy} className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 py-2.5 text-xs font-bold text-[#0E2240]"><Copy size={13} /> Copy</button>
          <button data-testid="creds-done-btn" onClick={onClose} className="rounded-xl bg-[#0E2240] py-2.5 text-xs font-bold text-white">Done</button>
        </div>
      </div>
    </div>
  );
}

function UserForm({ editUser, branches, onClose, onSaved, onCreds }) {
  const editing = !!editUser;
  const [f, setF] = useState(editing
    ? { name: editUser.name, phone: editUser.phone || "", email: editUser.email, username: editUser.username || "",
        role: editUser.role, branch_id: editUser.branch_id || "", branch_ids: editUser.branch_ids || [], password: "" }
    : { name: "", phone: "", email: "", username: "", role: "sales", branch_id: "", branch_ids: [], password: genPassword() });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const needsBranch = BRANCH_ROLES.includes(f.role);
  const needsZone = ZONE_ROLES.includes(f.role);
  const toggleCode = (id) => setF({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] });
  const suggestUsername = (name, branchId) => {
    const b = branches.find((x) => x.id === branchId);
    const base = name.trim().toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g, "");
    return base ? base + (b?.dealer_code ? "." + b.dealer_code.toLowerCase() : "") : "";
  };
  const save = async () => {
    try {
      if (editing) {
        const payload = { ...f };
        if (!payload.password) delete payload.password;
        if (!needsBranch) delete payload.branch_id;
        if (!needsZone) delete payload.branch_ids;
        await api.patch(`/users/${editUser.id}`, payload);
        toast.success("User updated — change is audit-logged");
        onClose(); onSaved();
      } else {
        const payload = { ...f };
        if (!needsBranch) delete payload.branch_id;
        if (!needsZone) delete payload.branch_ids;
        const r = await api.post("/users", payload);
        onClose(); onSaved();
        onCreds({ title: `${r.data.name} created`, username: f.username || r.data.username, password: f.password });
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div data-testid="user-form-modal" className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold text-[#0E2240]">{editing ? `Edit ${editUser.name}` : "Add User"}</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="col-span-2"><label className={lbl}>Full name *</label>
            <input data-testid="user-name-input" value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value, ...(editing ? {} : { username: suggestUsername(e.target.value, f.branch_id) }) })}
              className={inputCls} /></div>
          <div><label className={lbl}>Phone (10-digit) *</label>
            <input data-testid="user-phone-input" value={f.phone} onChange={set("phone")} placeholder="9847000000" maxLength={10} className={inputCls} /></div>
          <div><label className={lbl}>Email *</label>
            <input data-testid="user-email-input" type="email" value={f.email} onChange={set("email")} className={inputCls} /></div>
          <div><label className={lbl}>Role *</label>
            <select data-testid="user-role-select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          {needsBranch && (
            <div><label className={lbl}>Branch *</label>
              <select data-testid="user-branch-select" value={f.branch_id}
                onChange={(e) => setF({ ...f, branch_id: e.target.value, ...(editing ? {} : { username: suggestUsername(f.name, e.target.value) }) })} className={inputCls}>
                <option value="">Select…</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{bLabel(b)}</option>)}
              </select></div>
          )}
          {needsZone && (
            <div className="col-span-2"><label className={lbl}>Dealer codes covered * <span className="normal-case text-slate-400">({f.branch_ids.length} selected — each code can have only one {f.role === "zh" ? "Zonal Head" : "Accounts Zonal"})</span></label>
              <div data-testid="user-dealer-codes" className="mt-1 grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto rounded-xl border border-slate-200 p-2">
                {branches.map((b) => (
                  <label key={b.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer ${f.branch_ids.includes(b.id) ? "bg-[#0E2240] text-white" : "bg-slate-50 text-slate-700"}`}>
                    <input data-testid={`user-dealer-code-${b.id}`} type="checkbox" checked={f.branch_ids.includes(b.id)} onChange={() => toggleCode(b.id)} className="accent-[#BB162B]" />
                    <span className="truncate">{bLabel(b)}</span>
                  </label>
                ))}
              </div></div>
          )}
          {!needsBranch && !needsZone && <div className="flex items-end pb-2 text-[10px] text-slate-400">Group-wide role — no dealer code needed</div>}
          <div><label className={lbl}>User ID / login *</label>
            <input data-testid="user-username-input" value={f.username} onChange={set("username")} placeholder="auto from name + code" className={inputCls} /></div>
          <div><label className={lbl}>{editing ? "New password (optional)" : "Initial password *"}</label>
            <div className="flex gap-1">
              <input data-testid="user-password-input" value={f.password} onChange={set("password")} placeholder={editing ? "Leave blank to keep" : ""} className={inputCls} />
              <button data-testid="user-genpw-btn" onClick={() => setF({ ...f, password: genPassword() })}
                className="mt-1 rounded-xl border border-slate-300 px-2.5 text-[10px] font-bold text-[#0E2240]">Gen</button>
            </div></div>
        </div>
        <p className="mt-2 text-[10px] text-slate-400">The user will be forced to change this password at first login.</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
          <button data-testid="user-save-btn" onClick={save} className="rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white">{editing ? "Save Changes" : "Create User"}</button>
        </div>
      </div>
    </div>
  );
}

function DeleteUserDialog({ check, onClose, onConfirm }) {
  const OPEN_LABELS = { open_bookings: "open booking(s)", open_trips: "trip(s) in progress", open_transfers: "open transfer request(s)" };
  const open = Object.entries(check.open_items).filter(([, n]) => n);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div data-testid="user-delete-dialog" className="w-full max-w-sm rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-[#0E2240]">Delete {check.user.name}?</h3>
        {open.length > 0 ? (
          <div className="mt-2 rounded-lg bg-red-50 p-2.5 text-xs text-[#BB162B]">
            <b>Cannot delete yet.</b> Still has {open.map(([k, n]) => `${n} ${OPEN_LABELS[k]}`).join(", ")}. Close or release them first.
          </div>
        ) : (
          <div className="mt-2 space-y-1.5 text-xs">
            <div className="rounded-lg bg-slate-50 p-2.5 text-slate-700">
              Login removed permanently. History kept on record: <b>{check.history.trips}</b> trips · <b>{check.history.bookings}</b> bookings · <b>{check.history.audit_entries}</b> audit entries.
            </div>
            {check.notes.map((n, i) => <div key={i} className="rounded-lg bg-amber-50 p-2.5 text-amber-800">{n}</div>)}
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 py-2.5 text-xs font-bold text-slate-500">Cancel</button>
          <button data-testid="user-delete-confirm-btn" disabled={!check.can_delete} onClick={onConfirm}
            className="rounded-xl bg-[#BB162B] py-2.5 text-xs font-bold text-white disabled:opacity-40">Delete User</button>
        </div>
      </div>
    </div>
  );
}

function KecImportPanel({ onImported }) {
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const downloadTemplate = async () => {
    const r = await api.get("/users/kec-import-template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "TDFMS_KEC_Users_Template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setPreview(null); setReport(null); setBusy(true);
    const rd = new FileReader();
    rd.onload = async () => {
      const b64 = rd.result.split(",")[1];
      setFileB64(b64);
      try {
        const r = await api.post("/users/kec-import", { file_base64: b64, confirm: false });
        setPreview(r.data);
      } catch (err) { toast.error(errMsg(err)); }
      setBusy(false);
    };
    rd.readAsDataURL(file);
    e.target.value = "";
  };
  const confirmImport = async () => {
    setBusy(true);
    try {
      const r = await api.post("/users/kec-import", { file_base64: fileB64, confirm: true });
      setReport(r.data); setPreview(null);
      toast.success(`${r.data.imported} KEC user(s) created`);
      onImported();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const downloadCreds = () => {
    const lines = ["Name,User ID,Initial Password,Branch"];
    (report.credentials || []).forEach((c) => lines.push(`"${c.name}",${c.username},${c.password},"${c.branch}"`));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tdfms_kec_credentials.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const ErrTable = ({ errors }) => (
    <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-red-100">
      <table className="w-full text-left text-[11px]">
        <thead className="bg-red-50 text-[9px] uppercase text-[#BB162B]"><tr><th className="p-1.5">Row</th><th className="p-1.5">Column</th><th className="p-1.5">Problem</th></tr></thead>
        <tbody>{errors.map((e, i) => <tr key={i} className="border-t border-red-50"><td className="p-1.5 font-bold">{e.row}</td><td className="p-1.5">{e.column}</td><td className="p-1.5 text-slate-600">{e.problem}</td></tr>)}</tbody>
      </table>
    </div>
  );
  return (
    <div data-testid="kec-import-panel" className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="font-heading text-sm font-bold text-[#0E2240]">Bulk KEC import (.xlsx — sheet "KEC Users")</h3>
      <p className="mt-1 text-[11px] text-slate-500">Creates one login per KEC under the branch's SM (matched by <b>Dealer Code</b>). User ID & password auto-generate when left blank — download the credentials sheet after import (shown once). Every KEC must change their password at first login.</p>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button data-testid="kec-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-[#0E2240]"><Download size={14} /> Download template</button>
        <label className="flex cursor-pointer items-center gap-2 rounded-full bg-[#0E2240] px-4 py-2 text-xs font-bold text-white">
          <Upload size={14} /> {busy ? "Validating…" : "Upload .xlsx"}
          <input data-testid="kec-import-file-input" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={pick} />
        </label>
      </div>
      {preview && (
        <div data-testid="kec-import-preview" className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
          <div className="font-bold text-[#0E2240]">{fileName}: {preview.total_rows} rows scanned · <span className="text-emerald-700">{preview.clean} clean</span> · <span className="text-[#BB162B]">{preview.failed} with errors</span></div>
          {preview.errors.length > 0 && <ErrTable errors={preview.errors} />}
          <div className="mt-3 flex gap-2">
            <button data-testid="kec-import-confirm-btn" onClick={confirmImport} disabled={busy || preview.clean === 0}
              className="flex-1 rounded-xl bg-[#BB162B] py-2.5 text-sm font-bold text-white disabled:opacity-40">Create {preview.clean} KEC user(s)</button>
            <button onClick={() => setPreview(null)} className="rounded-xl border border-slate-200 px-4 text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      )}
      {report && (
        <div data-testid="kec-import-result" className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
          <div className="font-bold text-emerald-700">{report.imported} KEC user(s) created — all must change password at first login</div>
          {report.failed > 0 && <div className="font-bold text-[#BB162B] mt-1">{report.failed} rejected — fix and re-upload (re-uploads are safe)</div>}
          {report.errors.length > 0 && <ErrTable errors={report.errors} />}
          {(report.credentials || []).length > 0 && (
            <button data-testid="kec-creds-download-btn" onClick={downloadCreds}
              className="mt-2 flex items-center gap-1.5 rounded-full bg-[#BB162B] px-3 py-1.5 text-[11px] font-bold text-white"><Download size={12} /> Download credentials (shown once)</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [flt, setFlt] = useState({ role: "", branch_id: "", status: "", q: "" });
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState(null);
  const [delCheck, setDelCheck] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const isAdmin = me.role === "admin";
  const load = () => {
    api.get("/users", { params: Object.fromEntries(Object.entries(flt).filter(([, v]) => v)) }).then((r) => setUsers(r.data)).catch(() => {});
    api.get("/users/coverage").then((r) => setGaps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [flt]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? `${u.name} reactivated` : `${u.name} deactivated — history stays attributed`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPw = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      setCreds({ title: `Password reset — ${u.name}`, username: u.username || u.email, password: r.data.temp_password });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (u) => {
    try { const { data } = await api.get(`/users/${u.id}/delete-check`); setDelCheck(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmDel = async () => {
    try { await api.delete(`/users/${delCheck.user.id}`); toast.success(`${delCheck.user.name} deleted — past activity stays on record`); setDelCheck(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const codesOf = (u) => (u.branch_ids || []).map((id) => branches.find((b) => b.id === id)?.dealer_code || id).join(", ");
  const selCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium";
  const roleLabel = (r) => ROLES.find(([v]) => v === r)?.[1] || r;

  return (
    <div className="mt-4">
      {gaps.length > 0 && (
        <div data-testid="coverage-gaps" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          <b>Vacant role slots:</b> {gaps.map((g) => `${g.label}: ${g.missing.join(", ")}`).join(" · ")}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap items-center flex-1">
          <div className="relative min-w-[160px]">
            <Search size={13} className="absolute left-3 top-2.5 text-slate-400" />
            <input data-testid="user-search-input" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="Name, email, phone…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs" />
          </div>
          <select data-testid="user-filter-role" value={flt.role} onChange={(e) => setFlt({ ...flt, role: e.target.value })} className={selCls}>
            <option value="">All roles</option>
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="user-filter-branch" value={flt.branch_id} onChange={(e) => setFlt({ ...flt, branch_id: e.target.value })} className={selCls}>
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{bLabel(b)}</option>)}
          </select>
          <select data-testid="user-filter-status" value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} className={selCls}>
            <option value="">All statuses</option><option value="active">Active</option><option value="deactivated">Deactivated</option>
          </select>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button data-testid="kec-import-toggle-btn" onClick={() => setShowImport((s) => !s)}
              className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-[#0E2240]"><Upload size={14} /> Bulk KEC Import</button>
            <button data-testid="user-add-btn" onClick={() => setForm("new")}
              className="flex items-center gap-1.5 rounded-full bg-[#BB162B] px-4 py-2.5 text-xs font-bold text-white"><Plus size={14} /> Add User</button>
          </div>
        )}
      </div>
      {isAdmin && showImport && <KecImportPanel onImported={load} />}
      <div className="mt-3 space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.username || u.id}`}
            className={`rounded-xl border p-3.5 ${u.active === false ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#0E2240] text-[13px]">{u.name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-600">{roleLabel(u.role)}</span>
                  {u.active === false && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold uppercase text-red-700">Deactivated</span>}
                  {u.must_change_password && u.active !== false && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-700">Pwd reset pending</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {u.username || u.email}{u.phone && ` · ${u.phone}`} · {u.email}
                  {u.branch_id && ` · ${bLabel(branches.find((b) => b.id === u.branch_id)) || u.branch_id}`}
                  {!u.branch_id && ZONE_ROLES.includes(u.role) && ` · covers ${codesOf(u) || "no dealer codes"}`}
                </div>
                <div className="text-[10px] text-slate-400">Last login: {dt(u.last_login)}</div>
              </div>
              {isAdmin && (
                <div className="flex gap-1.5">
                  <button data-testid={`user-edit-${u.username || u.id}`} onClick={() => setForm(u)} title="Edit"
                    className="rounded-lg border border-slate-200 p-2 text-[#0E2240]"><Pencil size={13} /></button>
                  <button data-testid={`user-resetpw-${u.username || u.id}`} onClick={() => resetPw(u)} title="Reset password"
                    className="rounded-lg border border-slate-200 p-2 text-slate-500"><KeyRound size={13} /></button>
                  {u.id !== me.id && (
                    <>
                      <button data-testid={`user-toggle-${u.username || u.id}`} onClick={() => toggleActive(u)}
                        className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${u.active === false ? "bg-emerald-600 text-white" : "border border-red-200 text-[#BB162B]"}`}>
                        {u.active === false ? "Reactivate" : "Deactivate"}
                      </button>
                      <button data-testid={`user-delete-${u.username || u.id}`} onClick={() => del(u)}
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-bold text-slate-400">Delete</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {users.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">No users match</div>}
      </div>
      {form && <UserForm editUser={form === "new" ? null : form} branches={branches}
        onClose={() => setForm(null)} onSaved={load} onCreds={setCreds} />}
      {creds && <CredsDialog creds={creds} onClose={() => setCreds(null)} />}
      {delCheck && <DeleteUserDialog check={delCheck} onClose={() => setDelCheck(null)} onConfirm={confirmDel} />}
    </div>
  );
}
